import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, ArrowLeft, ArrowRight } from "lucide-react";

// Homepage "Riversol in Action" — full-width, swipeable reuse of the exact
// PDP v2 video design (9:16 cards, hover play button, drag/arrow carousel,
// full-screen modal). Placeholder clips are the 5 verified Riversol CDN
// videos + 2 repeated for a fuller row. The client will supply the real
// homepage clips one by one to replace these.
const CDN = "https://www.riversol.com/cdn/shop";
const clip = (id, thumbV, vidPath) => ({
  id,
  thumb: `${CDN}/files/preview_images/${id}.thumbnail.0000000000.jpg?v=${thumbV}&width=800`,
  video: `${CDN}/videos/c/vp/${id}/${vidPath}`,
});

const VIDEOS = [
  clip("6707819debae4417a2990f0a1b9730ce", "1781381205", "6707819debae4417a2990f0a1b9730ce.HD-720p-3.0Mbps-86478555.mp4?v=0"),
  clip("ed6efc4b6f0f4be1b72ed0a865cfc660", "1781381205", "ed6efc4b6f0f4be1b72ed0a865cfc660.HD-720p-1.6Mbps-86478567.mp4?v=0"),
  clip("4c038977d26547408999f4e1fb48a347", "1773956868", "4c038977d26547408999f4e1fb48a347.HD-720p-1.6Mbps-78302330.mp4?v=0"),
  clip("72bd9868884948778c77cf98530fb4f1", "1772641115", "72bd9868884948778c77cf98530fb4f1.HD-1080p-2.5Mbps-77141232.mp4?v=0"),
  clip("610be86236774523b276bd7efa018af8", "1773956866", "610be86236774523b276bd7efa018af8.HD-720p-1.6Mbps-78302326.mp4?v=0"),
  // Repeated placeholders (unique keys) — to be replaced by client uploads.
  { ...clip("6707819debae4417a2990f0a1b9730ce", "1781381205", "6707819debae4417a2990f0a1b9730ce.HD-720p-3.0Mbps-86478555.mp4?v=0"), id: "6707819debae4417a2990f0a1b9730ce-r1" },
  { ...clip("4c038977d26547408999f4e1fb48a347", "1773956868", "4c038977d26547408999f4e1fb48a347.HD-720p-1.6Mbps-78302330.mp4?v=0"), id: "4c038977d26547408999f4e1fb48a347-r2" },
];

export const RiversolInActionHome = () => {
  const [active, setActive] = useState(null);
  const n = VIDEOS.length;
  const trackRef = useRef(null);
  const drag = useRef({ down: false, startX: 0, startScroll: 0, moved: false });
  const [current, setCurrent] = useState(0);

  const stepPx = () => {
    const el = trackRef.current;
    if (!el) return 0;
    const card = el.querySelector("[data-vcard]");
    return card ? card.getBoundingClientRect().width + 20 /* gap */ : el.clientWidth * 0.4;
  };

  const updateCurrent = () => {
    const el = trackRef.current;
    if (!el) return;
    const s = stepPx();
    if (!s) return;
    setCurrent(Math.round(el.scrollLeft / s) % n);
  };

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateCurrent, { passive: true });
    return () => el.removeEventListener("scroll", updateCurrent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const scrollByCards = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const s = stepPx();
    const max = el.scrollWidth - el.clientWidth;
    if (dir > 0 && el.scrollLeft >= max - 4) {
      el.scrollTo({ left: 0, behavior: "smooth" });
      return;
    }
    if (dir < 0 && el.scrollLeft <= 4) {
      el.scrollTo({ left: max, behavior: "smooth" });
      return;
    }
    el.scrollBy({ left: dir * s, behavior: "smooth" });
  };

  const onPointerDown = (e) => {
    const el = trackRef.current;
    if (!el) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    drag.current = { down: true, startX: e.clientX, startScroll: el.scrollLeft, moved: false, captured: false, pointerId: e.pointerId };
  };
  const onPointerMove = (e) => {
    if (!drag.current.down) return;
    const el = trackRef.current;
    const dx = e.clientX - drag.current.startX;
    if (Math.abs(dx) > 6) {
      if (!drag.current.captured) {
        drag.current.captured = true;
        drag.current.moved = true;
        el.setPointerCapture?.(drag.current.pointerId);
        el.classList.add("cursor-grabbing");
      }
      el.scrollLeft = drag.current.startScroll - dx;
    }
  };
  const endDrag = () => {
    const el = trackRef.current;
    if (drag.current.captured) {
      try { el?.releasePointerCapture?.(drag.current.pointerId); } catch {}
      el?.classList.remove("cursor-grabbing");
    }
    drag.current.down = false;
  };
  const onClickCapture = (e) => {
    if (drag.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      drag.current.moved = false;
    }
  };

  return (
    <section className="bg-riversol-paper py-24 lg:py-28 border-y border-neutral-200" data-testid="home-in-action">
      <div className="w-full px-6 lg:px-10">
        <div className="flex items-end justify-between mb-10 lg:mb-14">
          <div>
            <div className="hidden font-mono text-[10px] tracking-[0.28em] uppercase text-neutral-500">
              Real routines
            </div>
            <h2 className="mt-4 max-w-[720px] text-[32px] sm:text-[38px] lg:text-[46px] leading-[1.06] tracking-[-0.02em] text-neutral-900">
              Riversol <span className="italic text-riversol-blue">in action.</span>
            </h2>
          </div>
          <span className="font-mono text-[11px] tracking-[0.24em] uppercase text-neutral-400 hidden md:block">
            {String(n).padStart(2, "0")} clips
          </span>
        </div>

        <div
          ref={trackRef}
          className="flex gap-5 overflow-x-auto scroll-smooth snap-x snap-mandatory cursor-grab select-none hide-scrollbar pb-2"
          style={{ scrollbarWidth: "none" }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onPointerLeave={endDrag}
          onClickCapture={onClickCapture}
          data-testid="home-in-action-track"
        >
          {VIDEOS.map((v, i) => (
            <button
              key={v.id}
              data-vcard
              onClick={() => setActive(v)}
              data-testid={`home-ugc-video-${i}`}
              aria-label="Play video"
              className="group relative snap-start shrink-0 basis-[calc((100%-20px)/2.2)] sm:basis-[calc((100%-40px)/3)] lg:basis-[calc((100%-4*20px)/5)] aspect-[9/16] editorial-frame bg-riversol-stone block"
            >
              <img src={v.thumb} alt="" draggable={false} className="w-full h-full object-cover img-zoom" loading="lazy" />
              <span className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="flex items-center justify-center w-16 h-16 rounded-full border border-white/70 bg-white/10 backdrop-blur-md text-white opacity-85 transition-all duration-300 group-hover:opacity-100 group-hover:scale-110 group-hover:bg-white group-hover:border-white group-hover:text-riversol-blue group-hover:shadow-[0_10px_30px_rgba(0,0,0,0.25)]">
                  <Play size={18} className="fill-current ml-0.5" />
                </span>
              </span>
              <span className="absolute bottom-4 left-4 font-mono text-[10px] tracking-[0.2em] uppercase text-white/90">
                {String(i + 1).padStart(2, "0")}
              </span>
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between mt-8">
          <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-500">
            {String(current + 1).padStart(2, "0")} / {String(n).padStart(2, "0")}
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={() => scrollByCards(-1)}
              data-testid="home-ugc-prev"
              aria-label="Previous clip"
              className="w-11 h-11 rounded-full border border-neutral-300 flex items-center justify-center text-neutral-900 bg-white hover:bg-riversol-blue hover:border-riversol-blue hover:text-white transition-all"
            >
              <ArrowLeft size={16} />
            </button>
            <button
              onClick={() => scrollByCards(1)}
              data-testid="home-ugc-next"
              aria-label="Next clip"
              className="w-11 h-11 rounded-full border border-neutral-300 flex items-center justify-center text-neutral-900 bg-white hover:bg-riversol-blue hover:border-riversol-blue hover:text-white transition-all"
            >
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setActive(null)}
            data-testid="home-ugc-modal"
          >
            <motion.div
              className="relative"
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setActive(null)}
                aria-label="Close"
                data-testid="home-ugc-modal-close"
                className="absolute -top-12 right-0 text-white/80 hover:text-white transition-colors flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase"
              >
                Close <X size={18} />
              </button>
              <video
                key={active.id}
                src={active.video}
                controls
                autoPlay
                playsInline
                className="h-[80vh] max-h-[900px] w-auto max-w-[92vw] rounded-sm bg-black shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
                style={{ aspectRatio: "9 / 16" }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default RiversolInActionHome;
